import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from '../../assets/styles/DishRefusalModal.module.scss';

/**
 * Modal para o Garçom registrar a recusa ou pedido de ajuste de um prato já entregue.
 */
const DishRefusalModal = ({ dish, order, itemIndex, onClose, onSubmit }) => {
  const [refusalType, setRefusalType] = useState(''); // 'ajuste' | 'recusado'
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (typeof onClose === 'function') onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleSelectType = (type) => {
    setRefusalType(type);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 50);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!refusalType) return;
    if (!reason.trim() || reason.trim().length < 3) return;

    try {
      setIsSubmitting(true);
      if (typeof onSubmit === 'function') {
        await onSubmit({
          dish,
          order,
          itemIndex,
          refusalType,
          refusalReason: reason.trim(),
        });
      }
    } catch (err) {
      console.error('Erro ao enviar recusa de prato:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = Boolean(refusalType && reason.trim().length >= 3);

  const modalContent = (
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget && typeof onClose === 'function' && !isSubmitting) {
          onClose();
        }
      }}
    >
      <div className={styles.modalCard} role="dialog" aria-modal="true">
        <div className={styles.header}>
          <div className={styles.iconWrapper}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <h2 className={styles.title}>Prato Recusado / Ajuste</h2>
          <p className={styles.subtitle}>
            Item: <strong>{dish?.name || 'Prato'}</strong>
            {order?.tableNumber || order?.mesa ? ` • Mesa ${order.tableNumber || order.mesa}` : ''}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Seletor de Tipo */}
          <div className={styles.optionsSection}>
            <span className={styles.sectionLabel}>1. Selecione o tipo de solicitação:</span>
            <div className={styles.optionCards}>
              <div
                className={`${styles.optionCard} ${refusalType === 'ajuste' ? styles.selectedAjuste : ''}`}
                onClick={() => handleSelectType('ajuste')}
                role="button"
                tabIndex={0}
              >
                <span className={styles.optionIcon}>🛠️</span>
                <span className={styles.optionTitle}>Ajuste</span>
                <span className={styles.optionDesc}>Ponto da carne, esquentar ou alterar preparo</span>
              </div>

              <div
                className={`${styles.optionCard} ${refusalType === 'recusado' ? styles.selectedRecusado : ''}`}
                onClick={() => handleSelectType('recusado')}
                role="button"
                tabIndex={0}
              >
                <span className={styles.optionIcon}>🚫</span>
                <span className={styles.optionTitle}>Recusado</span>
                <span className={styles.optionDesc}>Cliente não aceitou ou desistiu do item</span>
              </div>
            </div>
          </div>

          {/* Campo de Texto para Justificativa */}
          <div className={styles.inputSection}>
            <label htmlFor="refusal-reason" className={styles.sectionLabel}>
              2. Justificativa para a Cozinha:
            </label>
            <textarea
              id="refusal-reason"
              ref={textareaRef}
              className={styles.textarea}
              disabled={!refusalType || isSubmitting}
              placeholder={
                !refusalType
                  ? 'Selecione "Ajuste" ou "Recusado" acima para habilitar este campo...'
                  : refusalType === 'ajuste'
                  ? 'Ex: O cliente solicitou a carne mais bem passada / esquentar o prato...'
                  : 'Ex: O cliente não gostou do tempero / prato entregue por engano...'
              }
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              required
            />
            <span className={`${styles.helperText} ${refusalType ? styles.activeHelper : ''}`}>
              {!refusalType
                ? 'Campo desabilitado até a seleção do tipo'
                : reason.trim().length < 3
                ? 'Informe pelo menos 3 caracteres descrevendo o motivo'
                : 'Pronto para enviar à cozinha'}
            </span>
          </div>

          {/* Ações */}
          <div className={styles.footerActions}>
            <button
              type="button"
              className={styles.btnCancel}
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={styles.btnSubmit}
              disabled={!isFormValid || isSubmitting}
            >
              {isSubmitting ? 'Enviando...' : 'Enviar para a Cozinha'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  if (typeof document !== 'undefined') {
    return createPortal(modalContent, document.body);
  }

  return modalContent;
};

export default DishRefusalModal;
